<template>
  <ModalNuevo no-padding
              :open="open"
              class="allergen-modal"
              @close="handleClose">

    <div class="allergen-modal__container">

      <div class="header">
        <h4 class="product-title">{{ product.title }}</h4>
      </div>

      <div class="no-allergens" v-if="!product.allergens.length">
        {{ t('allergens.allergens_unavailable') }}
      </div>

      <div class="allergens" v-if="product.allergens.length">
        <div class="allergen-header">
          <h5 class="allergen-header__title">{{ t('allergens.header.title') }}</h5>

          <span v-if="product.allergens.length > 0"
                class="confirmation-status confirmed">{{ t('allergens.header.confirmed') }} </span>
          <span v-if="product.allergens.length < 1" class="confirmation-status">{{
              t('allergens.header.unconfirmed')
            }} </span>
        </div>

        <div class="allergen" v-for="allergen in product.allergens" :key="allergen.id">
          <img v-if="allergen.icon" :src="allergen.icon" :alt="allergen.slug" class="allergen-icon">
          <Icon v-else name="info-bold" class="allergen-icon"/>

          <p>{{ translateApi(allergen.description) }}</p>
        </div>
      </div>

      <div class="notice">{{ t('allergens.notice') }}</div>

    </div>
  </ModalNuevo>
</template>

<script setup lang="ts">
import ModalNuevo from '@/components/Modals/ModalNuevo/ModalNuevo.vue'
import type { Product } from '@/types/http/EstablishmentDistribution'
import Icon from '@/components/partials/Icon.vue'
import { useI18n } from 'vue-i18n'
import { useApiTranslations } from '@/composables/useApiTranslation'

const {t} = useI18n()
const {translateApi} = useApiTranslations()

defineProps<{
  product: Product,
  open: boolean,
}>()

const emit = defineEmits(['update:modelValue'])

const handleClose = () => {
  emit('update:modelValue')
}

</script>

<style lang="scss" scoped>
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

.allergen-modal {

  &__container {
    padding:        2rem;
    display:        flex;
    flex-direction: column;
    gap:            1rem;

    .header {
      display:        flex;
      flex-direction: column;
      gap:            1rem 0;

      .product-title {
        @include line-lg;
        padding:     0;
        font-weight: 700;
        font-size:   1.25rem;
      }
    }

    .no-allergens {
      font-size:     1rem;
      font-weight:   500;

      padding:       .25rem .5rem;
      border-radius: .5rem;
      background:    var(--color-error);
      color:         var(--color-error-text);
      width:         fit-content;
    }

    .allergens {
      .allergen-header {
        display:         flex;
        align-items:     center;
        gap:             0 1rem;

        &__title {
          font-size: 1.125rem;
        }

        .confirmation-status {
          background-color: rgb(207, 247, 225);
          color:            rgb(36, 46, 48);

          font-weight:      500;
          padding:          .1rem .5rem;
          border-radius:    .5rem;
          font-size:        .75rem;

          &.confirmed {
            background-color: rgb(207, 247, 225);
            color:            rgb(36, 46, 48);
          }
        }
      }

      display:        flex;
      flex-direction: column;

      .allergen {
        display:       flex;
        gap:           0.8rem;
        margin-bottom: .5rem;

        .allergen-icon {
          :deep(svg) {
            height: 1.5rem;
            width:  1.5rem;
          }

          height:        1.5rem;
          width:         1.5rem;
          border-radius: 999rem;
        }
      }
    }

    .notice {
      margin-top:    1rem;
      padding:       1.5rem 1.5rem;
      font-size:     0.95rem;
      border-radius: .5rem;
      background:    #F7F7F9;
    }
  }

  &__info {
    padding: 2rem;

    @include lg-up {
      padding: 3rem;
    }
  }

  &__description {
    padding:   0 0 1.25rem;
    color:     var(--color-neutral-secondary);
    font-size: 1rem;
  }

  &__surcharge {
    padding:   0;
    color:     var(--color-neutral-secondary);
    font-size: 0.9rem;
  }

  &__heading {
    font-size:   1.125rem;
    font-weight: 900;
    margin:      0 0 1rem 0;
  }
}
</style>